import { Dialog } from "primereact/dialog";
import React, { useState, useEffect, useRef } from "react";
import { ILicense, ILicensesAddOc, IVideoContent } from "@/models";
import { DropdownField } from "../../../index";
import {
  capitalizeFirstLetter,
  toastError,
  toastSuccess,
} from "../../../../utils/index";
import { useGetLicensesOfContentData } from "../../../../hooks/index";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import moment from "moment";
import { Button } from "primereact/button";
import { addLicenseToContent } from "../../../../services";

interface AddRemoveLicenseToContentDialogProps {
  visible: boolean;
  content: IVideoContent;
  isLoading: boolean;
  licenses: ILicense[] | undefined;
  onHide: () => void;
}

const AddRemoveLicenseToContentDialog: React.FC<AddRemoveLicenseToContentDialogProps> = ({
  visible,
  content,
  isLoading,
  licenses,
  onHide,
}) => {
  const toast = useRef<Toast>(null);
  const { licensesOfContentData, getLicensesOfContentIsLoading, refetchGetLicensesOfContent } =
    useGetLicensesOfContentData(content?.id);

  const [selectedLicense, setSelectedLicense] = useState(0);
  const [isAddLoading, setIsAddLoading] = useState(false);

  const [addLicenseContent, setAddLicenseContent] = useState<ILicensesAddOc>({
    cId: 0,
    lcIds: [0],
    username: "manager",
    userID: 0,
  });

  useEffect(() => {
    if (content?.id) {
      setAddLicenseContent(prev => ({ ...prev, cId: content.id }));
    }
  }, [content?.id]);

  const hideDialog = () => {
    setSelectedLicense(-1);
    onHide();
  };

  const addLicense = async (licenseId: number, contentId: number) => {
    setIsAddLoading(true);
    try {
      const contentGroupObject: ILicensesAddOc = {
        cId: contentId,
        lcIds: [licenseId],
        username: "admin",
        userID: 0
      };
      await addLicenseToContent(contentGroupObject);
      await refetchGetLicensesOfContent();
      toastSuccess(toast, "License Added Successfully");
      setSelectedLicense(-1);
    } catch (error) {
      toastError(toast, "Failed to Add License");
    } finally {
      setIsAddLoading(false);
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        visible={visible}
        style={{ width: "70%" }}
        header={"License Relations of " + content?.name}
        modal
        className="p-fluid"
        onHide={hideDialog}
      >
        <div className="col-12" style={{ display: "flex" }}>
          <div className="col-6 xl:col-6">
            <DropdownField
              id="licenses"
              name="name"
              value={selectedLicense}
              onChange={(e) => setSelectedLicense(e.target.value)}
              options={licenses?.map((license) => ({
                value: license.id,
                label: license.name,
              }))}
              labelName={"Licenses"}
              labelText={capitalizeFirstLetter("Licenses")}
            />
            <Button
              label="Add"
              onClick={() => addLicense(selectedLicense, content?.id)}
              className="p-button-warning"
              disabled={selectedLicense == -1 || isAddLoading ? true : false}
            />
          </div>
        </div>

        <div className="col-12 xl:col-12">
          <div className="card">
            <h5>Licenses Of Content</h5>
            <DataTable
              value={licensesOfContentData}
              rows={5}
              paginator
              responsiveLayout="scroll"
              loading={getLicensesOfContentIsLoading}
            >
              <Column
                field="name"
                header="License Name"
                sortable
                style={{ width: "25%" }}
              />
              <Column
                field="startDate"
                header="Start Date"
                sortable
                style={{ width: "30%" }}
                body={(rowData) =>
                  rowData.startDate && (
                    <>{moment(rowData.startDate).format("DD/MM/YYYY")}</>
                  )
                }
              />
              <Column
                field="endDate"
                header="End Date"
                sortable
                style={{ width: "30%" }}
                body={(rowData) =>
                  rowData.endDate && (
                    <>{moment(rowData.endDate).format("DD/MM/YYYY")}</>
                  )
                }
              />
            </DataTable>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AddRemoveLicenseToContentDialog;
